import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import * as API from 'api';
import { useMutation } from 'react-query';
import { Box, Modal, Typography, Button, IconButton } from '@material-ui/core';
import { CircularProgress } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons';

const Parties = API.Parties();
const WaitForJob = API.WaitForJob;

const styles = theme => ({
  dialogFooter: {
    padding: theme.spacing(1.5),
  },
  centerFlex: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    width: '40rem',
    padding: '1rem 1.5rem',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    padding: '1.5rem',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  modelBodyText: {
    padding: '0',
  },
  error: {
    color: 'red',
  },
  matchingArtistsTable: {
    width: '100%',
    overflowY: 'scroll',
    maxHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 1px 24px -1px rgba(0, 0, 0, 0.1)',
  },
  matchingArtistsTableHead: {
    display: 'flex',
    fontWeight: 700,
    backgroundColor: '#fafafa',
  },
  matchingArtistsCell: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '25%',
    height: '2.5rem',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '0 0.25rem',
  },
  matchingArtistsRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  matchingArtistList: {
    border: '1px solid #fafafa',
    textOverflow: 'none',
  },
  selectArtistButton: {
    boxShadow: '0',
  },
  linkIcon: {
    width: '0.75rem',
    height: '0.75rem',
  },
});

const AppleArtistIdModal = ({
  onSaveAppleArtistId,
  asset_party_id,
  full_name,
  isOpen,
  onClose,
  onClickAppleLink,
  classes,
}) => {
  const createNewAppleArtistIdMutation = useMutation(({ asset_party_id, forceCreateToken = null }) =>
    Parties.getOrCreateAppleId(asset_party_id, forceCreateToken)
  );

  const [isLoading, setIsLoading] = useState(false);
  const [matchingArtists, setMatchingArtists] = useState(null);
  const [forceCreateToken, setForceCreateToken] = useState(null);
  const [appleError, setAppleError] = useState({});

  const handleClose = () => {
    onClose();
    setMatchingArtists(null);
    setForceCreateToken(null);
  };

  const bodyText = !isLoading && !matchingArtists?.length && (
    <Typography className={classes.modelBodyText} variant="body2">
      Click below to &apos;get or create&apos; an Apple Artist ID. If existing artists with the same name are found,
      they will become selectable here.
    </Typography>
  );

  const createNewAppleArtistId = async e => {
    e.preventDefault();
    setIsLoading(true);

    const createAppleIdJob = await createNewAppleArtistIdMutation.mutateAsync({
      asset_party_id,
      full_name,
      forceCreateToken,
    });
    let appleRes;

    try {
      appleRes = await WaitForJob(createAppleIdJob).then(res => res.data);
    } catch (error) {
      setAppleError({ otherError: true, text: error.data.detail });
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    if (appleRes?.newAppleId) {
      onSaveAppleArtistId(appleRes?.newAppleId);
      handleClose();
      return;
    }

    if (appleRes?.forceCreateToken && appleRes?.data) {
      setMatchingArtists(Object.values(appleRes?.data));
      setForceCreateToken(appleRes.forceCreateToken);
    }

    if (appleRes?.alreadyCreateByUs) {
      setAppleError({ alreadyCreateByUs: true });
    } else {
      setAppleError({});
    }
  };

  const matchingArtistsRows = (matchingArtists || [])
    .filter(({ name }) => !['Name', '--------', ' '].includes(name))
    .map(artist => {
      const handleClickAppleLink = () => {
        onClickAppleLink(artist.apple_id);
      };

      const selectArtistButton = (
        <Box className={classes.matchingArtistsCell} style={{ justifyContent: 'flex-end', paddingRight: '1rem' }}>
          <Button
            className={classes.selectArtistButton}
            size="small"
            variant="contained"
            onClick={() => {
              onSaveAppleArtistId(artist.apple_id);
              handleClose();
            }}>
            Select
          </Button>
        </Box>
      );
      const name = (
        <Box className={classes.matchingArtistsCell}>
          <IconButton className={classes.linkIcon} size="small" onClick={handleClickAppleLink}>
            <OpenInNewIcon className={classes.linkIcon} />
          </IconButton>
          <Typography className={classes.modelBodyText} style={{ marginLeft: '0.25rem' }} variant="body2">
            {artist.name}
          </Typography>
        </Box>
      );

      const genre = <Box className={classes.matchingArtistsCell}>{artist.genre}</Box>;
      const createdAt = <Box className={classes.matchingArtistsCell}>{artist.created_at?.split(' ')?.[0]}</Box>;
      return (
        <Box className={classes.matchingArtistsRow} key={artist.apple_id}>
          {name}
          {createdAt}
          {genre}
          {selectArtistButton}
        </Box>
      );
    });

  const actionButton = !forceCreateToken ? (
    <Button variant="contained" disabled={isLoading} onClick={createNewAppleArtistId}>
      {isLoading ? `Attempting to Generate Apple ID...` : `Generate Apple ID`}
    </Button>
  ) : (
    <Button
      variant="contained"
      style={{ backgroundColor: '#FF335F', color: 'white' }}
      disabled={isLoading}
      onClick={createNewAppleArtistId}>
      {`Force Generate a New Apple ID`}
    </Button>
  );

  const matchingArtistsSection = (
    <>
      <Typography style={{ marginBottom: '1.5rem' }}>
        Select an existing artist to link, or force create a new Apple Artist ID.
      </Typography>
      <Box className={classes.matchingArtistsTable}>
        <Box className={classes.matchingArtistsTableHead}>
          {['Artist Name', 'Created At', 'Genre', ''].map((label, i) => (
            <Box className={classes.matchingArtistsCell} key={`table-head-${label}`}>
              {label}
            </Box>
          ))}
        </Box>
        <Box className={classes.matchingArtistsBody}>{matchingArtistsRows}</Box>
      </Box>
    </>
  );

  const headerText = (
    <Typography id="modal-modal-title" variant="h5" component="h2">
      {forceCreateToken ? 'Link' : 'Create'} Apple ID: {full_name}
    </Typography>
  );

  const closeButton = (
    <Button disabled={isLoading} className={classes.closeButton} onClick={handleClose} variant="text">
      x
    </Button>
  );

  const ErrorText = props => (
    <Typography className={classes.error} variant="subtitle2">
      {props.children}
    </Typography>
  );

  return (
    <Modal open={isOpen} onClose={isLoading ? null : handleClose} className={classes.centerFlex}>
      <Box className={classes.modalContainer}>
        <Box className={classes.modalHeader}>
          {headerText}
          {closeButton}
        </Box>
        <Box className={classes.modalBody}>
          {bodyText}
          {isLoading && <CircularProgress />}
          {forceCreateToken && matchingArtistsSection}
          {createNewAppleArtistIdMutation.isError && <ErrorText>{createNewAppleArtistIdMutation.error}</ErrorText>}
          {appleError.alreadyCreateByUs && <ErrorText>{'Apple said an Id is already created by us.'}</ErrorText>}
          {appleError.otherError && <ErrorText>{appleError.text}</ErrorText>}
        </Box>
        {actionButton}
      </Box>
    </Modal>
  );
};

export default withStyles(styles)(AppleArtistIdModal);
